import { STEP_COUNT, isQueuedStatus, isRunningStatus, stageWord, stepOf } from './job-stage';

export { STEP_COUNT, isQueuedStatus, isRunningStatus, stageWord, stepOf } from './job-stage';

/** The fields of one run of a multi-manufacturing project the job progress reads. */
export interface JobChildSource {
  id: string;
  label?: string | null;
  caseLabel?: string | null;
  materialName?: string | null;
  status: number;
  status_progress?: number | null;
  queued?: boolean;
}

/** The fields of a project list row the job progress reads. */
export interface JobSource {
  id: string;
  name: string;
  status: number;
  status_progress?: number | null;
  projectType?: string | null;
  config?: { runType?: number } | null;
  aws_lambda_job_status?: number | null;
  generation_status?: string | null;
  children?: readonly JobChildSource[] | null;
}

export type JobPhase = 'running' | 'queued';
export type JobRunState = 'running' | 'queued' | 'done' | 'error' | 'idle';

/** One run of a multi-manufacturing project. */
export interface JobRun {
  id: string;
  title: string;
  state: JobRunState;
  /** Stage word while running. */
  stage: string | null;
  /** 0–100 while running. */
  percent: number | null;
}

export interface JobRunCounts {
  /** Runs taking part in this round: running, queued, finished or stopped. */
  total: number;
  done: number;
  running: number;
  queued: number;
  errors: number;
}

/** What the status cell and the runs list show for a running or queued job. */
export interface JobProgress {
  phase: JobPhase;
  /** "Preparing", "Optimizing", "Post-processing", "Finishing", "Generating" or "Queued". */
  stage: string;
  /** Overall 0–100, or null while nothing has started. */
  percent: number | null;
  /** Current step 0–3 for the step dots, or null when there are no dots. */
  step: number | null;
  /** Jobs ahead in the local queue, or null when unknown. */
  ahead: number | null;
  /** Run counts for a multi-manufacturing project, else null. */
  runs: JobRunCounts | null;
  /** Per-run breakdown for a multi-manufacturing project, else empty. */
  runList: JobRun[];
}

/** Looks up how many jobs are ahead of a project in the local queue. */
export type AheadLookup = (projectId: string) => number | null;

function clampPercent(value: number | null | undefined): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** "under 1 min", "12 min", "1 h 5 min". */
export function formatMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (minutes < 1) return 'under 1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

export function aheadText(ahead: number): string {
  if (ahead <= 0) return 'Next in line';
  return `${ahead} ${ahead === 1 ? 'job' : 'jobs'} ahead`;
}

const SEPARATORS = /^[\s·—–:|,-]+|[\s·—–:|,-]+$/g;

/**
 * A run's title: its case, plus whatever its own label adds. Run labels often
 * repeat the case and the material already ("Casting (Y+) — Aluminum"), so
 * those parts are taken out rather than shown twice.
 */
export function runTitle(child: Pick<JobChildSource, 'label' | 'caseLabel' | 'materialName'>): string {
  const caseLabel = child.caseLabel?.trim() || null;
  let extra = (child.label ?? '').trim();
  for (const part of [caseLabel, child.materialName?.trim() || null]) {
    if (!part) continue;
    // Whole words only, so a short material name never cuts into another word.
    const escaped = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    extra = extra.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'iu'), ' ');
  }
  extra = extra.replace(/\s+/g, ' ').replace(SEPARATORS, '').trim();
  if (!caseLabel) return extra || (child.label ?? '').trim();
  return extra ? `${caseLabel} · ${extra}` : caseLabel;
}

/** A run's state; a waiting run that was never dispatched (offline HPC) is idle. */
export function runState(child: Pick<JobChildSource, 'status' | 'queued'>): JobRunState {
  if (isRunningStatus(child.status)) return 'running';
  if (child.status === 4) return 'done';
  if (child.status === 2) return 'error';
  // Desktop children may carry no `queued` flag: their waiting runs sit in the local queue.
  if (isQueuedStatus(child.status) && child.queued !== false) return 'queued';
  return 'idle';
}

/** True for a project with several runs. */
export function isMultiRun(item: Pick<JobSource, 'children'>): boolean {
  return (item.children?.length ?? 0) >= 2;
}

function singleProgress(item: JobSource, ahead: AheadLookup | null): JobProgress | null {
  const status = Number(item.status);
  const base = { runs: null, runList: [] };
  if (isRunningStatus(status)) {
    const percent = clampPercent(item.status_progress);
    const generating = item.projectType === 'soft_jaws' || item.projectType === 'mold';
    return {
      ...base,
      phase: 'running',
      stage: stageWord(status, item.config?.runType, item.projectType),
      percent,
      step: generating ? null : stepOf(status),
      ahead: null,
    };
  }
  if (isQueuedStatus(status)) {
    return { ...base, phase: 'queued', stage: 'Queued', percent: null, step: null, ahead: ahead?.(item.id) ?? null };
  }
  return null;
}

function multiProgress(item: JobSource, ahead: AheadLookup | null): JobProgress | null {
  const children = item.children ?? [];
  const runType = item.config?.runType;
  const runList: JobRun[] = children.map((child) => {
    const state = runState(child);
    return {
      id: child.id,
      title: runTitle(child),
      state,
      stage: state === 'running' ? stageWord(child.status, runType, item.projectType) : null,
      percent: state === 'running' ? clampPercent(child.status_progress) : null,
    };
  });
  const counts: JobRunCounts = { total: 0, done: 0, running: 0, queued: 0, errors: 0 };
  let sum = 0;
  for (const run of runList) {
    if (run.state === 'idle') continue;
    counts.total += 1;
    if (run.state === 'running') {
      counts.running += 1;
      sum += run.percent ?? 0;
    } else if (run.state === 'queued') {
      counts.queued += 1;
    } else {
      if (run.state === 'done') counts.done += 1;
      else counts.errors += 1;
      sum += 100;
    }
  }
  if (counts.running === 0 && counts.queued === 0) return null;

  const percent = counts.total ? Math.round(sum / counts.total) : 0;
  const running = children.filter((c) => runState(c) === 'running');
  const lead = running.reduce<JobChildSource | null>((best, c) => {
    if (!best) return c;
    if (c.status !== best.status) return c.status > best.status ? c : best;
    return (c.status_progress ?? 0) > (best.status_progress ?? 0) ? c : best;
  }, null);
  const phase: JobPhase = lead ? 'running' : 'queued';
  let aheadCount: number | null = null;
  if (phase === 'queued' && ahead) {
    for (const child of children) {
      if (runState(child) !== 'queued') continue;
      const n = ahead(child.id);
      if (n !== null && (aheadCount === null || n < aheadCount)) aheadCount = n;
    }
  }
  return {
    phase,
    stage: lead ? stageWord(lead.status, runType, item.projectType) : 'Queued',
    percent: phase === 'queued' && percent === 0 ? null : percent,
    // Parallel runs sit on different steps, so the dots show only for one running run.
    step: lead && counts.running === 1 ? stepOf(lead.status) : null,
    ahead: aheadCount,
    runs: counts,
    runList,
  };
}

/**
 * The progress of a running or queued job, or null when the project has no
 * job going (the table then shows its usual status badge). A project with
 * several runs is summed up: finished and stopped runs count as 100%, queued
 * runs as 0%.
 */
export function jobProgress(item: JobSource, ahead: AheadLookup | null = null): JobProgress | null {
  if (item.aws_lambda_job_status === 1) return null;
  if (item.generation_status === 'pending' || item.generation_status === 'running') return null;
  // A Ready project runs nothing, whatever a stale run list still says.
  if (Number(item.status) === 1) return null;
  return isMultiRun(item) ? multiProgress(item, ahead) : singleProgress(item, ahead);
}

/** The first line: the stage, plus how many runs are done for a multi-run job. */
export function jobHeadline(job: JobProgress): string {
  if (!job.runs) return job.stage;
  return `${job.stage} · ${job.runs.done} of ${job.runs.total} done`;
}

/**
 * The time line: the place in the local queue while a job waits. A running job
 * shows none - its stage, percent and step dots say where it is, and a time
 * estimate from the percent misleads because the steps differ in length.
 */
export function jobTimeText(job: JobProgress): string {
  return job.phase === 'queued' && job.ahead !== null ? aheadText(job.ahead) : '';
}

export function errorText(count: number): string {
  return count === 1 ? '1 error' : `${count} errors`;
}

/** The progress bar's spoken value, e.g. "42%, step 2 of 4". */
export function jobValueText(job: JobProgress): string {
  const parts = [`${job.percent ?? 0}%`];
  if (job.step !== null) parts.push(`step ${job.step + 1} of ${STEP_COUNT}`);
  return parts.join(', ');
}

/** One run's state in words, e.g. "Optimizing 42%". */
export function runStateText(run: JobRun): string {
  switch (run.state) {
    case 'running':
      return `${run.stage} ${run.percent ?? 0}%`;
    case 'queued':
      return 'Queued';
    case 'done':
      return 'Finished';
    case 'error':
      return 'Error';
    default:
      return 'Not started';
  }
}

/** The running percentages of a job: one per running run. */
export function runningPercents(item: JobSource): number[] {
  if (item.aws_lambda_job_status === 1) return [];
  if (isMultiRun(item)) {
    return (item.children ?? []).filter((c) => runState(c) === 'running').map((c) => clampPercent(c.status_progress));
  }
  return isRunningStatus(item.status) ? [clampPercent(item.status_progress)] : [];
}
